/* Move the existing Judge.me name/title into the photo caption; never copy or replace review data. */
(() => {
  const selector = '[data-ns-judgeme-editorial]';
  const observers = new WeakMap();
  function mount(root) {
    if (observers.has(root)) return;
    let config = { cards: [] };
    try { config = JSON.parse(root.querySelector('[data-ns-review-products]')?.textContent || '{"cards":[]}'); } catch (_) { /* Keep the original widget if settings are unavailable. */ }
    const bindings = new Map(config.cards.map(card => [card.id, card]));
    if (config.sectionVideo?.id && config.sectionVideo.video?.sources?.length) {
      const current = bindings.get(config.sectionVideo.id) || { id: config.sectionVideo.id };
      bindings.set(config.sectionVideo.id, { ...current, video: config.sectionVideo.video, preferCustomVideo: true });
    }
    const customerPhotos = new WeakMap();
    // The homepage carousel omits Judge.me's full-review video markup. These IDs
    // belong to its pinned review cards and were checked against /pages/reviews.
    const customerVideos = new Map(Object.entries({
      '40540954-058a-4736-92c9-fac180b879e6': '6ce5071b-805a-43ab-9cb8-e187d8193b73',
      '443b536b-f8e6-406a-ad5f-72f43a5b0662': 'd0ca91cc-29d5-4f05-90bc-10439b971f6c',
      '61e6a1f6-618a-4d1a-ad64-9dbbe01b8fce': 'bdfed011-a276-40fb-80f2-e9c5d5de7cb5',
      '70a915fa-92fe-4c85-aaf4-2330bb5a1c88': 'e05be1ab-c797-45b6-9eb9-c7e787dbe3cf',
      '7beecb07-0b72-434f-bfbd-4093a5b1f679': 'f687ad50-44a6-4069-8295-8f626570699b',
      '9833c6b8-3946-449e-b840-5118aa6a78ad': '581ffa89-27bb-46ae-a2dc-3bedb041d95a',
      'e4a523a1-4ed0-4386-880e-085ab456095f': 'a62f1c2b-0678-4d26-9bd4-61404e469136'
    }));
    const isCustomerPhoto = value => {
      if (!value) return false;
      try { return new URL(value, location.href).hostname === 'review-images.judgeme.com'; }
      catch (_) { return false; }
    };
    const syncPhoto = (photo, binding, reviewId) => {
      if (!photo) return;
      const nativePhoto = [photo.getAttribute('data-src-retina'), photo.getAttribute('data-src'), photo.getAttribute('src'), photo.currentSrc].find(isCustomerPhoto);
      if (nativePhoto) customerPhotos.set(photo, { id: reviewId, url: nativePhoto });
      const remembered = customerPhotos.get(photo);
      const customerPhoto = remembered?.id === reviewId ? remembered.url : null;
      const preferred = customerPhoto || binding?.image;
      if (!preferred) return;
      // Preserve Judge.me lazy loading; never eagerly load every off-screen review.
      if (photo.getAttribute('src') && photo.getAttribute('src') !== preferred) photo.setAttribute('src', preferred);
      if (photo.getAttribute('data-src') !== preferred) photo.setAttribute('data-src', preferred);
      if (photo.getAttribute('data-src-retina') !== preferred) photo.setAttribute('data-src-retina', preferred);
      if (photo.hasAttribute('srcset') && (!customerPhoto || !photo.getAttribute('srcset').includes('review-images.judgeme.com'))) photo.removeAttribute('srcset');
      if (!customerPhoto) photo.alt = binding?.product?.title || 'Review card image';
    };
    const players = new Map();
    const bunnyId = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    let disposed = false;
    const rememberCustomerVideos = () => {
      document.querySelectorAll('.jdgm-full-rev[data-review-id] .jdgm-vid-player[data-external-id]').forEach(player => {
        const reviewId = player.closest('.jdgm-full-rev')?.dataset.reviewId;
        const videoId = player.getAttribute('data-external-id');
        if (reviewId && (bunnyId.test(videoId || '') || /^\d+$/.test(videoId || ''))) customerVideos.set(reviewId, videoId);
      });
    };
    // Only image thumbnails enter the viewport preload zone; players have no src
    // and do not exist until a visitor explicitly presses Play.
    const nearbyMedia = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const state = players.get(entry.target);
        if (state?.poster && !state.cover.src) state.cover.src = state.poster;
        nearbyMedia.unobserve(entry.target);
      });
    }, { rootMargin: '600px' });
    const removeMedia = media => {
      const state = players.get(media);
      if (state) {
        if (state.native) state.player?.pause();
        state.player?.remove();
        players.delete(media);
      }
      nearbyMedia.unobserve(media); media.remove();
    };
    const syncVideo = (card, image, binding, reviewId) => {
      const customerVideo = customerVideos.get(reviewId);
      const customVideo = binding?.video?.sources?.find(source => source.mime_type === 'video/mp4' || source.format === 'mp4');
      const useCustom = customVideo && (binding.preferCustomVideo || !customerVideo);
      let media = image.querySelector('.ns-review-inline-media');
      const source = useCustom ? customVideo.url : bunnyId.test(customerVideo || '')
        ? 'https://iframe.mediadelivery.net/embed/748386/' + customerVideo + '?preload=false&autoplay=true'
        : /^\d+$/.test(customerVideo || '') ? 'https://player.vimeo.com/video/' + customerVideo + '?autoplay=1&title=0&byline=0&portrait=0' : null;
      if (!source) { if (media) removeMedia(media); image.classList.remove('ns-review-has-video'); return; }
      if (media?.dataset.src === source) return;
      if (media) removeMedia(media);
      image.classList.add('ns-review-has-video');
      media = document.createElement('div'); media.className = 'ns-review-inline-media'; media.dataset.src = source;
      const cover = document.createElement('img'); cover.className = 'ns-review-video-cover'; cover.alt = ''; cover.decoding = 'async';
      const button = document.createElement('button'); button.className = 'ns-review-video-play'; button.type = 'button';
      button.setAttribute('aria-label', 'Play customer review video');
      const poster = useCustom ? binding.video.poster : bunnyId.test(customerVideo || '')
        ? 'https://review-videos.judgeme.com/' + customerVideo + '/thumbnail.jpg' : binding?.videoCover;
      const state = { media, cover, poster, native: !!useCustom, player: null };
      players.set(media, state);
      cover.addEventListener('load', () => media.classList.add('ns-review-cover-ready'));
      cover.addEventListener('error', () => media.classList.remove('ns-review-cover-ready'));
      button.addEventListener('click', event => {
        event.preventDefault(); event.stopPropagation();
        if (disposed || !media.isConnected || state.player) return;
        const player = document.createElement(useCustom ? 'video' : 'iframe');
        state.player = player;
        if (useCustom) {
          player.controls = true; player.playsInline = true; player.preload = 'none';
          player.setAttribute('aria-label', 'Customer review video');
          player.addEventListener('playing', () => media.classList.add('ns-review-video-playing'));
          player.addEventListener('error', () => { state.player = null; player.remove(); });
          player.src = source;
          media.prepend(player);
          player.play().catch(() => media.classList.add('ns-review-video-playing'));
        } else {
          player.title = 'Customer review video';
          player.allow = 'autoplay; fullscreen; picture-in-picture; encrypted-media'; player.allowFullscreen = true;
          player.addEventListener('load', () => media.classList.add('ns-review-video-playing'));
          player.addEventListener('error', () => { state.player = null; player.remove(); });
          player.src = source;
          media.prepend(player);
        }
      });
      media.append(cover, button); image.prepend(media);
      nearbyMedia.observe(media);
    };
    const enhance = () => {
      players.forEach((state, media) => { if (!root.contains(media)) removeMedia(media); });
      const rating = root.querySelector('.jdgm-all-reviews-rating');
      const count = root.querySelector('.jdgm-carousel-number-of-reviews');
      const score = Number(rating?.dataset.score), total = Number(count?.dataset.numberOfReviews);
      if (score > 0 && score <= 5 && total > 0) {
        let summary = root.querySelector('.ns-review-score');
        if (!summary) {
          summary = document.createElement('div'); summary.className = 'ns-review-score';
          summary.append(document.createElement('span'), document.createElement('strong'), document.createElement('span'));
          root.querySelector('.jdgm-carousel-wrapper')?.prepend(summary);
          root.classList.add('ns-review-header-ready');
        }
        const values = ['Rated', score + ' / 5', 'from ' + total.toLocaleString('en-US') + ' reviews'];
        [...summary.children].forEach((child, index) => { if (child.textContent !== values[index]) child.textContent = values[index]; });
      }
      root.querySelectorAll('.jdgm-carousel-item__inner-wrapper').forEach(card => {
        const image = card.querySelector('.jdgm-carousel-item__image-wrapper');
        const reviewer = card.querySelector('.jdgm-carousel-item__reviewer-wrapper');
        if (!image) return;
        if (reviewer && !image.querySelector('.ns-review-caption')) {
          const caption = document.createElement('div');
          caption.className = 'ns-review-caption';
          caption.append(reviewer);
          const title = card.querySelector('.jdgm-carousel-item__review-title');
          if (title && title.textContent.trim()) caption.append(title);
          image.append(caption);
        }
        const reviewId = card.closest('[data-review-id]')?.dataset.reviewId;
        const binding = bindings.get(reviewId);
        syncPhoto(image.querySelector('img:not(.ns-review-video-cover)'), binding, reviewId);
        syncVideo(card, image, binding, reviewId);
        if (!binding || card.querySelector('.ns-review-product')) return;
        if (binding.product) {
          const link = document.createElement('a');
          link.className = 'ns-review-product';
          link.href = binding.product.url;
          link.setAttribute('aria-label', (config.linkLabel || 'View saber') + ': ' + binding.product.title);
          if (binding.product.image) {
            const thumb = document.createElement('img');
            thumb.src = binding.product.image; thumb.alt = ''; thumb.width = 44; thumb.height = 44; thumb.loading = 'lazy';
            link.append(thumb);
          }
          const label = document.createElement('span');
          const name = document.createElement('strong'); name.textContent = binding.product.title;
          const action = document.createElement('small'); action.textContent = (config.linkLabel || 'View saber') + ' ↗';
          label.append(name, action); link.append(label);
          link.addEventListener('click', event => event.stopPropagation());
          card.querySelector('.jdgm-carousel-item__body')?.append(link);
        }
      });
    };
    const observer = new MutationObserver(enhance);
    observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-score', 'data-number-of-reviews', 'src', 'data-src', 'data-src-retina', 'srcset', 'data-external-id'] });
    // Judge.me can share full-review media between two carousels on the same page.
    const mediaObserver = new MutationObserver(records => {
      if (records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 && (node.matches('.jdgm-full-rev, .jdgm-vid-player') || node.querySelector('.jdgm-full-rev, .jdgm-vid-player'))))) {
        rememberCustomerVideos(); enhance();
      }
    });
    mediaObserver.observe(document.body, { childList: true, subtree: true });
    observers.set(root, { disconnect() { disposed = true; observer.disconnect(); mediaObserver.disconnect(); nearbyMedia.disconnect(); players.forEach((_, media) => removeMedia(media)); } });
    rememberCustomerVideos();
    enhance();
  }
  function init(container = document) {
    if (container.matches?.(selector)) mount(container);
    container.querySelectorAll?.(selector).forEach(mount);
  }
  init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    const root = event.target.querySelector(selector);
    observers.get(root)?.disconnect();
    if (root) observers.delete(root);
  });
})();
